import { useCallback, useEffect, useRef } from "react";
import { msalInstance, loginRequest } from "../../config/msalConfig";
import AppApi from "../../api/AppApi";
import { SsoMember } from "../../generated/dashboard-client";

interface UseMicrosoftAuthProps {
  onSuccess: (member: SsoMember) => void;
  onStartLoading: () => void;
  onError?: (message: string) => void;
}

export function useMicrosoftAuth({ onSuccess, onStartLoading, onError }: UseMicrosoftAuthProps) {
  const processed = useRef(false);
  const initPromise = useRef<Promise<void> | null>(null);

  const ensureInitialized = useCallback(() => {
    if (!initPromise.current) {
      initPromise.current = msalInstance.initialize();
    }
    return initPromise.current;
  }, []);

  const handleRedirectResult = useCallback(async () => {
    try {
      await ensureInitialized();
      if (window.location.href.indexOf("code=") === -1) return;
      if (processed.current) return;
      processed.current = true;
      const result = await msalInstance.handleRedirectPromise();
      if (result?.idToken) {
        onStartLoading();
        const resp = await AppApi.getMicrosoftMember(result.idToken);
        onSuccess(resp.data);
      }
    } catch (e: any) {
      processed.current = false;
      const code = e?.response?.data?.message || "";
      onError?.(
        code === "org_not_enabled"
          ? "Your organization isn't enabled for Microsoft sign-in. Please contact your administrator."
          : "We couldn't sign you in with Microsoft. Please try again."
      );
      console.error("Microsoft auth error:", e);
    }
  }, [ensureInitialized, onSuccess, onStartLoading, onError]);

  useEffect(() => {
    handleRedirectResult();
  }, [handleRedirectResult]);

  const loginWithMicrosoft = useCallback(async () => {
    try {
      await ensureInitialized();
      await msalInstance.loginRedirect(loginRequest);
    } catch (e) {
      console.error("Microsoft login error:", e);
    }
  }, [ensureInitialized]);

  return { loginWithMicrosoft };
}
